// Gate de lanzamiento (waitlist de clientes fundadores).
// Mientras config/launch.clientsOpen === false, los usuarios con rol client ven
// esta pantalla en vez de la app: se les asigna su número de fundador (callable
// claimFounderSpot, server-side) y se les avisará por correo al abrir.
// Talleres, mecánicos y admin pasan normal (son la captación). Rutas públicas
// (landing, login, pricing...) no se bloquean. Sin doc config/launch o ante
// error, la app queda ABIERTA (comportamiento actual).
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { loadLaunchConfig } from '../utils/launchConfig';
import { snapshotMigrationConfig } from '../utils/migrationConfig';
import { claimFounderSpot as claimFounderSpotApi } from '../api/users';
import { isPublicPath } from '../utils/publicRoutes';
import { Rocket, LogOut, Mail, Award, Check, Store } from 'lucide-react';
import FounderBadge from './FounderBadge';


export default function LaunchGate({ children }) {
  const { userProfile, user, profileLoaded } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [launch, setLaunch] = useState(null);
  const [founderNumber, setFounderNumber] = useState(null);
  const [claiming, setClaiming] = useState(false);
  // Un intento por sesión. Sin esto, el cliente que llega cuando el cupo YA está
  // lleno vuelve a llamar a la función en cada navegación (el servidor responde
  // sin número, así que no hay nada que apague la condición), y esa llamada se
  // factura. El servidor es idempotente; el derroche es lo que se evita aquí.
  const claimTried = useRef(false);

  // Re-lee la config al cambiar el usuario: sin sesión la lectura de config/
  // launch es denegada por reglas (cae a defaults "abierta" SIN cachear), así
  // que hay que reintentar cuando el login se complete o el gate nunca aplica.
  useEffect(() => {
    loadLaunchConfig().then(setLaunch);
  }, [user?.uid]);

  const role = userProfile?.role || userProfile?.userType || 'client';
  const path = location.pathname;
  const isPublic = isPublicPath(path);
  // Cuentas QA: correos en config/launch.qaEmails pasan el gate (siguen probando
  // la app completa con la waitlist activa). Se administra en /admin/launch.
  const isQa = !!user?.email &&
    (launch?.qaEmails || []).some(e => String(e).toLowerCase() === user.email.toLowerCase());
  // 🔴 EL ALTA DE UN NEGOCIO NO SE PUEDE BLOQUEAR CON LA LISTA DE ESPERA.
  //
  // La cabecera de este archivo dice que «talleres, mecánicos y admin pasan
  // normal (son la captación)», y sin embargo no pasaban: el dueño de un taller
  // NACE COMO CLIENTE — el rol solo cambia cuando el alta del negocio termina—,
  // así que el gate lo mandaba a la pantalla de espera ANTES de poder
  // registrarse. Con eso, la estrategia de «cierro a clientes y salgo a captar
  // talleres» era imposible: el negocio al que se le toca la puerta se queda
  // mirando una lista de espera.
  //
  // ⚠️ Va aquí y NO en `PUBLIC_PATHS`: esa lista exime de TODOS los gates,
  // incluido el de suspensión, así que un usuario sancionado podría dar de alta
  // un negocio. Y son las dos pantallas de alta exactas, no un prefijo: eximir
  // «todo lo que empiece por /workshop» abriría la app entera a cualquier
  // cliente que escribiera esa dirección.
  const esAltaDeNegocio = path === '/workshop/setup' || path === '/mechanic/setup';
  const gated = !!user && profileLoaded && launch && launch.clientsOpen === false
    && role === 'client' && !isPublic && !isQa && !esAltaDeNegocio;

  // Reclamar el número de fundador (idempotente, server-side).
  //
  // ⚠️ NO se condiciona a `gated` (2026-08-07). Antes solo se reclamaba desde la
  // pantalla de lista de espera, así que el día que se abrió la app el programa
  // de fundadores quedó apagado en la práctica: `gated` pasa a false para todos
  // y esta llamada no volvía a ocurrir nunca. Ahora lo pide cualquier cliente
  // autenticado que aún no tenga número, y es el SERVIDOR quien decide si queda
  // cupo (config/launch.clientFounderCap) y quién puede recibirlo. Es idempotente
  // y transaccional, así que una llamada de más no asigna dos números.
  const puedeReclamar = !!user && profileLoaded && role === 'client'
    && !userProfile?.founderNumber && !founderNumber;

  useEffect(() => {
    if (!puedeReclamar || claiming || claimTried.current) return;
    if (userProfile?.founderNumber) { setFounderNumber(userProfile.founderNumber); return; }
    if (founderNumber) return;
    claimTried.current = true;
    setClaiming(true);
    // 🔴 QUIÉN REPARTE EL NÚMERO DEPENDE DEL INTERRUPTOR, y la callable no puede
    // decidirlo por su cuenta: con `users` en Postgres responde
    // `failed-precondition` (escribir el número en la copia muerta consumiría un
    // número del contador para alguien cuyo perfil dirá `founder: false`). Desde
    // ese lado lo reparte `POST /users/me/founder-spot`, que es transaccional e
    // idempotente igual que la callable.
    //
    // La fuente se consulta AQUÍ DENTRO, en el momento de usarla, y no en una foto
    // tomada al montar. Ante cualquier duda, el camino de siempre.
    snapshotMigrationConfig()
      .then(snap => (snap.readsFrom('users') === 'postgres'
        ? claimFounderSpotApi()
        : httpsCallable(functions, 'claimFounderSpot')().then(res => res.data)))
      .then(data => { if (data?.founderNumber) setFounderNumber(data.founderNumber); })
      .catch(() => {})
      .finally(() => setClaiming(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeReclamar, userProfile?.founderNumber]);

  if (!gated) return children;

  const n = userProfile?.founderNumber || founderNumber;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99998,
      background: 'linear-gradient(135deg, #0b0b0f 0%, #1a1013 55%, #2a0e0c 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px', overflowY: 'auto',
    }}>
      <div style={{ maxWidth: '440px', width: '100%', textAlign: 'center', color: 'white' }}>
        <div style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.5px', marginBottom: '28px' }}>
          Meka<span style={{ color: '#E62117' }}>Fix</span>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '20px', padding: '36px 28px',
        }}>
          <Rocket size={44} color="#E62117" style={{ marginBottom: '14px' }} />
          <h1 style={{ margin: '0 0 10px', fontSize: '24px', fontWeight: 800 }}>
            {t('launch.waitTitle')}
          </h1>

          {n ? (
            <div style={{ margin: '6px 0 18px' }}>
              <FounderBadge size="lg" text={t('launch.founderNumber', { n })} />
            </div>
          ) : (
            <div style={{ height: '12px' }} />
          )}

          <p style={{ margin: '0 0 14px', fontSize: '15px', lineHeight: 1.6, color: 'rgba(255,255,255,0.85)' }}>
            {t('launch.waitBody')}
          </p>

          {/* Beneficios de Fundador — el emblema visible como símbolo de estatus */}
          <div style={{
            textAlign: 'left', margin: '4px 0 16px',
            background: 'rgba(255,201,77,0.08)', border: '1px solid rgba(255,201,77,0.35)',
            borderRadius: '14px', padding: '14px 16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '11px' }}>
              <span style={{
                width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                border: '2.5px solid #FFC94D', boxShadow: '0 0 10px rgba(255,201,77,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Award size={16} color="#FFC94D" />
              </span>
              <span style={{ fontSize: '13.5px', fontWeight: 800, color: '#FFD988', lineHeight: 1.3 }}>
                {t('launch.ringHighlight')}
              </span>
            </div>
            {/* 🔴 TRES BENEFICIOS DISTINTOS, Y EL MEJOR PRIMERO.
                Aquí se pintaban `benefit4` y `benefit2`, que son LA MISMA FRASE
                dicha dos veces («tu número de Fundador es único y tuyo de por
                vida» / «tu número de fundador, contigo de por vida»), y el pie
                de la pantalla la repetía una TERCERA vez. Mientras tanto
                `benefit1` —«Acceso desde el día 1 del lanzamiento, antes que el
                público», el argumento más fuerte del programa y el único que
                responde a «¿y yo qué gano por esperar?»— estaba escrito en los
                dos idiomas y no lo leía nadie. Ahora encabeza. */}
            {[t('launch.benefit1'), t('launch.benefit3'), t('launch.benefit2')].map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: i < 2 ? '7px' : 0 }}>
                <Check size={14} color="#FFC94D" style={{ flexShrink: 0, marginTop: '2px' }} />
                <span style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>{b}</span>
              </div>
            ))}
          </div>

          <p style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            margin: '0 0 6px', fontSize: '13px', color: 'rgba(255,255,255,0.6)',
          }}>
            <Mail size={14} /> {t('launch.waitEmailNote')}
          </p>
        </div>

        {/* 🔴 LA SALIDA DEL DUEÑO DE UN NEGOCIO.
            Esta pantalla solo ofrecía «cerrar sesión». Y el dueño de un taller
            NACE COMO CLIENTE —el rol solo cambia cuando termina el alta del
            negocio—, así que el que se registra tras una visita de calle
            aterriza aquí, lee «MekaFix abre pronto» y se va, que es justo la
            gente a la que se está saliendo a captar. `LaunchGate` ya exime
            `/workshop/setup` (ver arriba); lo que faltaba era decírselo.

            Es un enlace y no un `navigate()`: la pantalla es una toma completa
            del viewport y una navegación dura la deja atrás con la configuración
            releída, igual que hace el botón de cerrar sesión. */}
        <div style={{
          marginTop: '20px', padding: '16px 18px',
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '14px',
        }}>
          <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: '4px' }}>
            {t('launch.businessTitle')}
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '12.5px', lineHeight: 1.5, color: 'rgba(255,255,255,0.6)' }}>
            {t('launch.businessBody')}
          </p>
          <a
            href="/workshop/setup"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '11px 18px', borderRadius: '10px',
              background: '#E62117', color: '#fff', textDecoration: 'none',
              fontWeight: 700, fontSize: '14px',
            }}
          >
            <Store size={15} /> {t('launch.businessCta')}
          </a>
        </div>

        <button
          onClick={async () => { await signOut(auth); window.location.href = '/'; }}
          style={{
            marginTop: '22px', padding: '11px 22px',
            background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)',
            border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px',
            fontWeight: 600, fontSize: '14px', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '8px',
          }}
        >
          <LogOut size={15} /> {t('launch.logout')}
        </button>
      </div>
    </div>
  );
}
